import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { BUILDINGS, captureLocation, captureLocationRetrying, resolveShiftForDay } from '../lib/workerData';
import { formatDate, formatTime } from '../lib/utils';
import { s } from '../lib/workerStyles';
import InspectionScreen from '../components/worker/InspectionScreen';
import MessagesScreen from '../components/worker/MessagesScreen';
import ShiftTaskList, { TASK_LIST_BUILDINGS } from '../components/worker/ShiftTaskList';
import HelpGuide from '../components/HelpGuide';

const OPEN_SHIFT_KEY = 'tc_open_shift';
const OPEN_SHIFT_HOURS = 18;

function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (localStorage.getItem('tc_install_dismissed')) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  async function handleInstall() {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setShowBanner(false);
      setDeferredPrompt(null);
    }
  }

  function handleDismiss() {
    setShowBanner(false);
    setShowIOS(false);
    localStorage.setItem('tc_install_dismissed', '1');
  }

  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    if (isIOS && !isStandalone && !localStorage.getItem('tc_install_dismissed')) {
      setShowIOS(true);
    }
  }, [isIOS, isStandalone]);

  if (!showBanner && !showIOS) return null;

  return (
    <div style={{ background: '#1e3a5f', borderBottom: '1px solid #334155', padding: '12px 16px' }}>
      <div style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
        📲 Add TraverseClean to your home screen
      </div>
      {showIOS ? (
        <div style={{ color: '#94a3b8', fontSize: 12, lineHeight: 1.6 }}>
          Tap the <strong style={{ color: '#f1f5f9' }}>Share button</strong> (□↑) at the bottom of Safari, then tap <strong style={{ color: '#f1f5f9' }}>"Add to Home Screen"</strong>. This lets you open the app like any other app on your phone.
        </div>
      ) : (
        <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>
          Install for quick access — no browser or URL needed.
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        {!showIOS && (
          <button onClick={handleInstall}
            style={{ background: '#f97316', border: 'none', color: '#000', borderRadius: 6, padding: '7px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            Install App
          </button>
        )}
        <button onClick={handleDismiss}
          style={{ background: 'transparent', border: '1px solid #334155', color: '#64748b', borderRadius: 6, padding: '7px 14px', fontSize: 12, cursor: 'pointer' }}>
          Not now
        </button>
      </div>
    </div>
  );
}

// '17:30:00' -> '5:30pm', '08:00:00' -> '8am'
function formatClock(t) {
  if (!t) return null;
  const [rawH, rawM] = String(t).split(':');
  const h = parseInt(rawH, 10);
  if (Number.isNaN(h)) return null;
  const m = parseInt(rawM, 10) || 0;
  const suffix = h >= 12 && h < 24 ? 'pm' : 'am';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}${suffix}` : `${h12}:${String(m).padStart(2, '0')}${suffix}`;
}

// Every buildings row is created with these, so a row still carrying them tells
// us nothing — nobody chose them. Showing them would dress a column default up
// as this building's real hours, which is worse than the generic label below
// because it looks specific enough to be believed.
const DEFAULT_HOURS = { day: ['09:00', '17:30'], night: ['17:30', '02:00'] };

function hoursAreSet(hours, kind) {
  if (!hours) return false;
  const hhmm = (t) => (t ? String(t).slice(0, 5) : null);
  const [from, to] = kind === 'day'
    ? [hhmm(hours.day_start), hhmm(hours.day_end)]
    : [hhmm(hours.night_start), hhmm(hours.night_end)];
  if (!from || !to) return false;
  const [defFrom, defTo] = DEFAULT_HOURS[kind];
  return from !== defFrom || to !== defTo;
}

// Shift times are stored per building and are editable by the manager, so the
// label has to come from that data — otherwise changing a building's hours
// silently never reaches the crew's phones. Buildings whose hours nobody has set
// keep the labels they had before, so this only ever adds real information.
function shiftLabel(shift, buildingId, hours) {
  if (!shift) return null;

  const range = (from, to) => {
    const a = formatClock(from);
    const b = formatClock(to);
    return a && b ? `${a}–${b}` : null;
  };

  if (shift === 'day1' && hoursAreSet(hours, 'day')) {
    const r = range(hours.day_start, hours.day_end);
    if (r) return `Day Shift 1 · ${r}`;
  }
  if (shift === 'night' && hoursAreSet(hours, 'night')) {
    const r = range(hours.night_start, hours.night_end);
    if (r) return `Night Clean · ${r}`;
  }

  if (buildingId === 'K') {
    return shift === 'day1' ? 'Daytime Clean' : 'Evening Clean · 9:30pm';
  }
  if (shift === 'day1') return 'Day Shift 1 · 8am–3pm';
  if (shift === 'day2') return 'Day Shift 2 · 3pm–10pm';
  if (shift === 'night') return 'Night Clean · 10pm–6am';
  return null;
}

function workerDisplayName(profile, user) {
  return profile?.full_name || profile?.email || user?.email || 'Worker';
}

function saveOpenShift(row) {
  localStorage.setItem(OPEN_SHIFT_KEY, JSON.stringify({
    id: row.id,
    building_id: row.building_id,
    sign_in: row.sign_in,
    worker_name: row.worker_name,
  }));
}

function clearOpenShift() {
  localStorage.removeItem(OPEN_SHIFT_KEY);
}

function readOpenShiftLocal() {
  try {
    const raw = localStorage.getItem(OPEN_SHIFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Check-in GPS columns only — never used for out fields. */
function gpsInPayload(gps) {
  if (!gps || gps.lat == null) {
    return {
      gps_lat: null,
      gps_lng: null,
      gps_accuracy: null,
      gps_distance_metres: null,
      gps_verified: null,
      gps_error: gps?.error || null,
    };
  }
  return {
    gps_lat: gps.lat,
    gps_lng: gps.lng,
    gps_accuracy: gps.accuracy ?? null,
    gps_distance_metres: gps.distance_metres ?? null,
    gps_verified: gps.verified ?? false,
    gps_error: gps.error || null,
  };
}

/**
 * Keeps trying to get a GPS fix for a while after check-in already went
 * through with no reading — never touches sign_in, only backfills the
 * gps_* columns if a fix eventually comes in. Gives up silently after a
 * few tries; the row is left exactly as it was inserted (unverified, no
 * distance) if none of them land.
 */
async function retryGpsInBackground(rowId, building) {
  const RETRY_DELAYS_MS = [20000, 30000, 30000];
  for (const delay of RETRY_DELAYS_MS) {
    await new Promise(resolve => setTimeout(resolve, delay));
    const gps = await captureLocation(building);
    if (gps.lat != null) {
      await supabase
        .from('checklist_submissions')
        .update(gpsInPayload(gps))
        .eq('id', rowId);
      return;
    }
  }
}

/** Check-out GPS columns only — never copy check-in GPS. */
function gpsOutPayload(gps) {
  if (!gps || gps.lat == null) {
    return {
      gps_out_lat: null,
      gps_out_lng: null,
      gps_out_accuracy: null,
      gps_out_distance_metres: null,
      gps_out_verified: null,
      gps_out_error: gps?.error || 'gps_unavailable',
    };
  }
  return {
    gps_out_lat: gps.lat,
    gps_out_lng: gps.lng,
    gps_out_accuracy: gps.accuracy ?? null,
    gps_out_distance_metres: gps.distance_metres ?? null,
    gps_out_verified: gps.verified ?? false,
    gps_out_error: gps.error || null,
  };
}

function formatElapsed(signIn, now) {
  const mins = Math.max(0, Math.floor((now - new Date(signIn)) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m} min`;
  return `${h}h ${m}m`;
}

const RENTAL_DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const RENTAL_DAY_SHORT = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

function formatHHMM(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')}${period}`;
}

function formatRentalWindow(w) {
  if (w.start && w.end) return `${formatHHMM(w.start)}–${formatHHMM(w.end)}`;
  if (w.start) return `after ${formatHHMM(w.start)}`;
  return 'anytime';
}

// The Town's instructions for a building this week — set the alarm, lock the
// gates, don't clean the boardroom during council meetings. These are stored
// per cleaning slot and were previously shown nowhere at all, so a cleaner had
// no way to know an alarm was expected of them.
function uniqueWindowNotes(row) {
  return Array.from(new Set((row?.windows || []).map(w => w.note).filter(Boolean)));
}

// Groups a rental_schedules.windows array (one entry per day/slot) into
// per-day summary lines, e.g. "Mon 1:00PM–2:00PM, 9:00PM–10:00PM".
function formatRentalWindows(windows) {
  if (!Array.isArray(windows) || windows.length === 0) return [];
  const byDay = {};
  windows.forEach(w => {
    if (!w?.day) return;
    (byDay[w.day] = byDay[w.day] || []).push(formatRentalWindow(w));
  });
  return RENTAL_DAY_ORDER.filter(d => byDay[d]).map(d => `${RENTAL_DAY_SHORT[d]} ${byDay[d].join(', ')}`);
}

function BuildingSelectScreen({ profile, onSelect, onInspect, onLogout, onOpenMessages, onOpenHelp }) {
  const isSupervisor = profile?.role === 'supervisor' || profile?.role === 'manager';
  const assigned = profile?.assigned_buildings || [];
  const hasAssignments = assigned.length > 0;
  const [upcomingShifts, setUpcomingShifts] = useState([]);
  const [upcomingRentals, setUpcomingRentals] = useState([]);
  const [buildingHours, setBuildingHours] = useState({});
  // A worker can also see/select a building if they're personally assigned to it
  // via a rental_schedules row (Manager > Rentals), even if it's not in their
  // profile's assigned_buildings list.
  const rentalAssignedIds = new Set(upcomingRentals.map(r => r.building_id));

  // The Town's instructions travel with the building into the check-in screen,
  // so they're in front of the cleaner when they arrive rather than only on the
  // list they scrolled past.
  const notesForBuilding = (id) => uniqueWindowNotes(upcomingRentals.find(r => r.building_id === id));
  const selectBuilding = (b) => onSelect(b, notesForBuilding(b.id));
  // E and F are one combined entry (E); an old F assignment or rental still shows it.
  const canSee = (b) => isSupervisor || !hasAssignments || assigned.includes(b.id) || rentalAssignedIds.has(b.id)
    || (b.id === 'E' && (assigned.includes('F') || rentalAssignedIds.has('F')));
  const active = BUILDINGS.filter(b => b.status === 'active' && !b.mergedInto && canSee(b));
  const special = BUILDINGS.filter(b => b.status === 'special' && canSee(b));
  const inactive = BUILDINGS.filter(b => b.status === 'inactive' && canSee(b));

  useEffect(() => {
    async function loadShifts() {
      if (!profile?.id) return;
      const today = new Date().toISOString().split('T')[0];
      const nextWeek = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const [eventsRes, rentalsRes, hoursRes] = await Promise.all([
        supabase
          .from('scheduled_events')
          .select('*')
          .gte('end_date', today)
          .lte('start_date', nextWeek)
          .contains('assigned_workers', [profile.id])
          .order('start_date', { ascending: true }),
        supabase
          .from('rental_schedules')
          .select('*')
          .gte('week_start', today)
          .lte('week_start', nextWeek)
          .contains('assigned_workers', [profile.id])
          .order('week_start', { ascending: true }),
        supabase
          .from('buildings')
          .select('building_id, day_start, day_end, night_start, night_end'),
      ]);
      if (eventsRes.data) setUpcomingShifts(eventsRes.data);
      if (rentalsRes.data) setUpcomingRentals(rentalsRes.data);
      if (hoursRes.data) {
        setBuildingHours(Object.fromEntries(hoursRes.data.map(b => [b.building_id, b])));
      }
    }
    loadShifts();
  }, [profile]);

  function formatShiftDate(start, end) {
    const opts = { weekday: 'short', month: 'short', day: 'numeric' };
    const label = new Date(start + 'T12:00:00').toLocaleDateString('en-CA', opts);
    if (start === end) return label;
    return label + ' – ' + new Date(end + 'T12:00:00').toLocaleDateString('en-CA', opts);
  }

  return (
    <div style={s.screen}>
      <InstallPrompt />
      <div style={s.header}>
        <div style={s.logo}>
          <span style={s.logoMark}>TC</span>
          <div>
            <div style={s.logoName}>TraverseClean</div>
            <div style={s.logoSub}>Town of Paradise — TOP-25-27</div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <div style={s.dateChip}>{formatDate(new Date())}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#64748b', fontSize: 12 }}>{profile?.full_name}</span>
            <button style={s.msgBtn} onClick={onOpenMessages}>📨 Messages</button>
            <button style={s.msgBtn} onClick={onOpenHelp}>? Help</button>
            <button style={s.signOutBtn} onClick={onLogout}>Sign Out</button>
          </div>
        </div>
      </div>

      {upcomingShifts.length > 0 && (
        <>
          <div style={s.sectionLabel}>YOUR UPCOMING SHIFTS</div>
          <div style={{ padding: '0 12px 4px' }}>
            {upcomingShifts.map(ev => {
              const typeColors = { special_event: '#f97316', rental: '#3b82f6', heavy_rental_week: '#a78bfa' };
              const color = typeColors[ev.event_type] || '#64748b';
              return (
                <div key={ev.id} style={{ background: '#111827', border: '1px solid #1e293b', borderLeft: '3px solid ' + color, borderRadius: 10, padding: '12px 14px', marginBottom: 6 }}>
                  <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 14 }}>{ev.name}</div>
                  <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>📍 {ev.location}</div>
                  <div style={{ color: color, fontSize: 12, fontWeight: 600, marginTop: 4 }}>🗓 {formatShiftDate(ev.start_date, ev.end_date)}</div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {upcomingRentals.length > 0 && (
        <>
          <div style={s.sectionLabel}>YOUR BUILDINGS THIS WEEK</div>
          <div style={{ padding: '0 12px 4px' }}>
            {upcomingRentals.map(rental => {
              const b = BUILDINGS.find(x => x.id === rental.building_id);
              const displayName = b?.shortName || b?.name || rental.building_id;
              const dayLines = formatRentalWindows(rental.windows);
              return (
                <div key={rental.id} style={{ background: '#111827', border: '1px solid #1e293b', borderLeft: '3px solid #3b82f6', borderRadius: 10, padding: '12px 14px', marginBottom: 6 }}>
                  <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 14 }}>{displayName}</div>
                  <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>Week of {rental.week_start}</div>
                  {rental.min_staff > 1 && (
                    <div style={{ color: '#f59e0b', fontSize: 11, marginTop: 4 }}>👥 Min. {rental.min_staff} staff required</div>
                  )}
                  {dayLines.length > 0 && (
                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {dayLines.map(line => (
                        <div key={line} style={{ color: '#60a5fa', fontSize: 12 }}>🕐 {line}</div>
                      ))}
                    </div>
                  )}
                  {rental.notes && (
                    <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4, fontStyle: 'italic' }}>{rental.notes}</div>
                  )}
                  {uniqueWindowNotes(rental).map((n, i) => (
                    <div key={i} style={s.townNote}>{n}</div>
                  ))}
                </div>
              );
            })}
          </div>
        </>
      )}

      <div style={s.sectionLabel}>SELECT YOUR BUILDING</div>
      <div style={s.buildingList}>
        {active.map(b => {
          const label = shiftLabel(resolveShiftForDay(profile), b.id, buildingHours[b.id]);
          const notes = notesForBuilding(b.id);
          return (
            <div key={b.id}>
              <button style={s.buildingCard} onClick={() => selectBuilding(b)}>
                <div style={s.bId}>{b.id}</div>
                <div style={s.bBody}>
                  <div style={s.bName}>{b.name}</div>
                  <div style={s.bAddr}>{b.address}</div>
                  {b.leed && <span style={s.leedBadge}>🌿 LEED</span>}
                  {b.restricted && <span style={s.restrictedBadge}>⚠️ RESTRICTED</span>}
                  {label && (
                    <span style={{ display: 'block', marginTop: 4, fontSize: 11, color: '#60a5fa', fontWeight: 600 }}>🕐 {label}</span>
                  )}
                  {notes.map((n, i) => (
                    <span key={i} style={{ ...s.townNote, display: 'block' }}>{n}</span>
                  ))}
                </div>
                <div style={s.chevron}>›</div>
              </button>
              {isSupervisor && (
                <button
                  style={{ ...s.buildingCard, marginTop: -6, borderTop: 'none', borderTopLeftRadius: 0, borderTopRightRadius: 0, background: '#0c1322', justifyContent: 'center', padding: '8px 16px' }}
                  onClick={() => onInspect(b)}
                >
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#a78bfa' }}>🔍 Inspect this building</span>
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div style={s.sectionLabel}>SPECIAL EVENTS</div>
      <div style={s.buildingList}>
        {special.map(b => (
          <button key={b.id} style={{ ...s.buildingCard, borderColor: '#f97316' }} onClick={() => selectBuilding(b)}>
            <div style={{ ...s.bId, fontSize: 11 }}>SE</div>
            <div style={s.bBody}>
              <div style={s.bName}>{b.name}</div>
              <div style={s.bAddr}>{b.address}</div>
            </div>
            <div style={s.chevron}>›</div>
          </button>
        ))}
      </div>

      {inactive.length > 0 && (<>
        <div style={s.sectionLabel}>INACTIVE</div>
        <div style={s.buildingList}>
          {inactive.map(b => (
            <div key={b.id} style={{ ...s.buildingCard, opacity: 0.45, cursor: 'not-allowed' }}>
              <div style={{ ...s.bId, background: '#374151' }}>{b.id}</div>
              <div style={s.bBody}>
                <div style={s.bName}>{b.name}</div>
                <div style={{ ...s.bAddr, color: '#f87171' }}>{b.note}</div>
              </div>
            </div>
          ))}
        </div>
      </>)}
    </div>
  );
}

function ErrorBanner({ message, onRetry, busy }) {
  if (!message) return null;
  return (
    <div style={{ background: '#450a0a', borderBottom: '1px solid #7f1d1d', padding: '12px 16px' }}>
      <div style={{ color: '#fca5a5', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{message}</div>
      {onRetry && (
        <button
          onClick={onRetry}
          disabled={busy}
          style={{ background: '#f97316', border: 'none', color: '#000', borderRadius: 6, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}
        >
          {busy ? 'Retrying…' : 'Retry'}
        </button>
      )}
    </div>
  );
}

export default function WorkerApp() {
  const { profile, user, signOut } = useAuth();
  // select → checking_in → on_shift → checking_out → done (+ inspect)
  const [phase, setPhase] = useState('select');
  const [restoring, setRestoring] = useState(true);
  const [building, setBuilding] = useState(null);
  const [buildingNotes, setBuildingNotes] = useState([]);
  const [shift, setShift] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [maintOpen, setMaintOpen] = useState(false);
  const [maintNoteDraft, setMaintNoteDraft] = useState('');
  const [maintSaving, setMaintSaving] = useState(false);
  const [maintError, setMaintError] = useState(null);

  useEffect(() => {
    if (phase !== 'on_shift') return undefined;
    const t = setInterval(() => setNow(new Date()), 30000);
    setNow(new Date());
    return () => clearInterval(t);
  }, [phase]);

  useEffect(() => {
    async function restoreOpenShift() {
      if (!profile?.id) {
        setRestoring(false);
        return;
      }
      const since = new Date(Date.now() - OPEN_SHIFT_HOURS * 60 * 60 * 1000).toISOString();
      const { data, error: qErr } = await supabase
        .from('checklist_submissions')
        .select('id, worker_id, worker_name, building_id, sign_in, sign_out, maintenance_flag, maintenance_note')
        .eq('worker_id', profile.id)
        .is('sign_out', null)
        .gte('sign_in', since)
        .order('sign_in', { ascending: false })
        .limit(1);

      if (qErr) {
        const local = readOpenShiftLocal();
        if (local?.id) {
          const b = BUILDINGS.find(x => x.id === local.building_id) || { id: local.building_id, name: local.building_id, address: '' };
          setBuilding(b);
          setShift(local);
          setPhase('on_shift');
        }
        setRestoring(false);
        return;
      }

      const open = data?.[0];
      if (open) {
        const b = BUILDINGS.find(x => x.id === open.building_id) || { id: open.building_id, name: open.building_id, address: '' };
        setBuilding(b);
        setShift(open);
        saveOpenShift(open);
        setPhase('on_shift');
      } else {
        clearOpenShift();
      }
      setRestoring(false);
    }
    restoreOpenShift();
  }, [profile?.id]);

  function handleSelectBuilding(b, notes) {
    if (phase === 'on_shift' && shift?.id) return;
    setBuilding(b);
    setBuildingNotes(notes || []);
    setError(null);
    resetMaintUi();
    setPhase('checking_in');
  }

  function resetMaintUi() {
    setMaintOpen(false);
    setMaintNoteDraft('');
    setMaintError(null);
  }

  async function submitMaintenanceFlag() {
    if (!shift?.id || !maintNoteDraft.trim()) return;
    setMaintSaving(true);
    setMaintError(null);

    const { data, error: updErr } = await supabase
      .from('checklist_submissions')
      .update({ maintenance_flag: true, maintenance_note: maintNoteDraft.trim() })
      .eq('id', shift.id)
      .eq('worker_id', profile.id)
      .select('id, worker_id, worker_name, building_id, sign_in, sign_out, maintenance_flag, maintenance_note')
      .single();

    setMaintSaving(false);
    if (updErr) {
      setMaintError('Could not send: ' + updErr.message);
      return;
    }
    setShift(data);
    saveOpenShift(data);
    setMaintOpen(false);
  }

  function handleInspect(b) {
    setBuilding(b);
    setError(null);
    setPhase('inspect');
  }

  async function doCheckIn() {
    if (!profile?.id || !building) return;
    setBusy(true);
    setError(null);

    const since = new Date(Date.now() - OPEN_SHIFT_HOURS * 60 * 60 * 1000).toISOString();
    const { data: existing, error: existErr } = await supabase
      .from('checklist_submissions')
      .select('id, worker_name, building_id, sign_in, sign_out, maintenance_flag, maintenance_note')
      .eq('worker_id', profile.id)
      .is('sign_out', null)
      .gte('sign_in', since)
      .limit(1);

    if (existErr) {
      setError('Could not verify open shifts: ' + existErr.message);
      setBusy(false);
      return;
    }
    if (existing?.[0]) {
      const open = existing[0];
      const b = BUILDINGS.find(x => x.id === open.building_id) || building;
      setBuilding(b);
      setShift(open);
      saveOpenShift(open);
      setPhase('on_shift');
      setBusy(false);
      return;
    }

    const gps = await captureLocationRetrying(building);
    const row = {
      worker_id: profile.id,
      worker_name: workerDisplayName(profile, user),
      building_id: building.id,
      sign_in: new Date().toISOString(),
      ...gpsInPayload(gps),
    };

    const { data, error: insertErr } = await supabase
      .from('checklist_submissions')
      .insert([row])
      .select('id, worker_id, worker_name, building_id, sign_in, sign_out, maintenance_flag, maintenance_note')
      .single();

    if (insertErr) {
      setError('Check-in failed: ' + insertErr.message);
      setBusy(false);
      return;
    }

    // Check-in already went through above regardless of GPS — this only
    // keeps trying in the background to backfill a location if one never
    // came in, so a slow/cold GPS lock doesn't leave the row unverified
    // forever. sign_in never moves.
    if (gps.lat == null) {
      retryGpsInBackground(data.id, building);
    }

    setShift(data);
    saveOpenShift(data);
    setPhase('on_shift');
    setBusy(false);
  }

  async function doCheckOut() {
    if (!shift?.id || !building) return;
    setBusy(true);
    setError(null);
    setPhase('checking_out');

    const gps = await captureLocation(building);
    const { data, error: updateErr } = await supabase
      .from('checklist_submissions')
      .update({
        sign_out: new Date().toISOString(),
        ...gpsOutPayload(gps),
      })
      .eq('id', shift.id)
      .eq('worker_id', profile.id)
      .is('sign_out', null)
      .select('id, worker_id, worker_name, building_id, sign_in, sign_out, maintenance_flag, maintenance_note')
      .single();

    if (updateErr) {
      setError('Check-out failed: ' + updateErr.message);
      setPhase('on_shift');
      setBusy(false);
      return;
    }

    setShift(data);
    clearOpenShift();
    setPhase('done');
    setBusy(false);
  }

  function handleNewShift() {
    setBuilding(null);
    setShift(null);
    setError(null);
    resetMaintUi();
    setPhase('select');
  }

  if (restoring) {
    return (
      <div style={{ ...s.screen, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#64748b', fontSize: 14 }}>Loading…</div>
      </div>
    );
  }

  if (phase === 'inspect' && building) {
    return (
      <InspectionScreen
        building={building}
        profile={profile}
        onBack={() => setPhase('select')}
      />
    );
  }

  if (phase === 'checking_in' && building) {
    return (
      <div style={s.screen}>
        <div style={s.navBar}>
          <button style={s.backBtn} onClick={() => { if (!busy) { setPhase('select'); setError(null); } }}>‹</button>
          <div style={s.navTitle}>Check In</div>
          <div style={{ width: 40 }} />
        </div>
        <ErrorBanner message={error} onRetry={doCheckIn} busy={busy} />
        <div style={s.buildingHero}>
          <div style={building.id === 'SE' ? { ...s.heroId, background: '#f97316' } : s.heroId}>{building.id}</div>
          <div>
            <div style={s.heroName}>{building.name}</div>
            <div style={s.heroAddr}>{building.address}</div>
          </div>
        </div>
        <div style={s.formSection}>
          <div style={s.workerInfoBox}>
            <div style={s.workerInfoLabel}>CHECKING IN AS</div>
            <div style={s.workerInfoName}>{workerDisplayName(profile, user)}</div>
          </div>
          {buildingNotes.length > 0 && (
            <div style={s.townNotesBox}>
              <div style={s.townNotesLabel}>FROM THE TOWN THIS WEEK</div>
              {buildingNotes.map((n, i) => (
                <div key={i} style={s.townNoteLarge}>{n}</div>
              ))}
            </div>
          )}
          <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
            <div style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.5 }}>
              Location is recorded at check-in and check-out only — never during your shift.
            </div>
          </div>
          <button
            style={busy ? { ...s.startBtn, opacity: 0.6 } : s.startBtn}
            disabled={busy}
            onClick={doCheckIn}
          >
            {busy ? 'Checking in…' : 'Check In →'}
          </button>
        </div>
      </div>
    );
  }

  if ((phase === 'on_shift' || phase === 'checking_out') && building && shift) {
    return (
      <div style={s.screen}>
        <div style={s.header}>
          <div style={s.logo}>
            <span style={s.logoMark}>TC</span>
            <div>
              <div style={s.logoName}>On Shift</div>
              <div style={s.logoSub}>{workerDisplayName(profile, user)}</div>
            </div>
          </div>
        </div>
        <ErrorBanner message={error} onRetry={doCheckOut} busy={busy} />
        <div style={{ padding: 16 }}>
          <div style={{ background: '#052e16', border: '1px solid #166534', borderRadius: 14, padding: 20, marginBottom: 16 }}>
            <div style={{ color: '#86efac', fontSize: 12, fontWeight: 700, letterSpacing: 0.5, marginBottom: 8 }}>ON SITE NOW</div>
            <div style={{ color: '#f1f5f9', fontSize: 20, fontWeight: 800 }}>{building.id} — {building.shortName || building.name}</div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 6 }}>{building.address}</div>
            <div style={{ color: '#22c55e', fontSize: 15, fontWeight: 700, marginTop: 14 }}>
              In since {formatTime(shift.sign_in)}
            </div>
            <div style={{ color: '#f1f5f9', fontSize: 28, fontWeight: 800, marginTop: 10, letterSpacing: -0.5 }}>
              {formatElapsed(shift.sign_in, now)}
            </div>
            <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>elapsed</div>
          </div>
          <button
            style={busy ? { ...s.startBtn, opacity: 0.6, background: '#ef4444' } : { ...s.startBtn, background: '#ef4444' }}
            disabled={busy}
            onClick={doCheckOut}
          >
            {busy || phase === 'checking_out' ? 'Checking out…' : 'Check Out →'}
          </button>

          <div style={{ marginTop: 16 }}>
            <button
              style={shift.maintenance_flag ? s.maintFlagActive : s.maintFlagBtn}
              disabled={busy}
              onClick={() => {
                setMaintNoteDraft(shift.maintenance_note || '');
                setMaintError(null);
                setMaintOpen(o => !o);
              }}
            >
              🔧 {shift.maintenance_flag ? 'Issue reported — tap to add more' : 'Report an issue'}
            </button>
            {shift.maintenance_flag && !maintOpen && shift.maintenance_note && (
              <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 6, lineHeight: 1.4 }}>“{shift.maintenance_note}”</div>
            )}
            {maintOpen && (
              <>
                <textarea
                  style={s.maintInput}
                  rows={3}
                  placeholder="What's wrong — out of soap, broken fixture, anything Fabian should know"
                  value={maintNoteDraft}
                  onChange={e => setMaintNoteDraft(e.target.value)}
                  autoFocus
                />
                {maintError && <div style={{ color: '#f87171', fontSize: 12, marginTop: 6 }}>{maintError}</div>}
                <button
                  style={{ ...s.sendBtn, marginTop: 8, opacity: maintSaving || !maintNoteDraft.trim() ? 0.6 : 1 }}
                  disabled={maintSaving || !maintNoteDraft.trim()}
                  onClick={submitMaintenanceFlag}
                >
                  {maintSaving ? 'Sending…' : 'Send to Fabian'}
                </button>
              </>
            )}
          </div>

          {TASK_LIST_BUILDINGS.includes(building.id) && (
            <ShiftTaskList building={building} profile={profile} shiftId={shift.id} />
          )}
        </div>
      </div>
    );
  }

  if (phase === 'done' && building && shift) {
    const mins = Math.max(0, Math.round((new Date(shift.sign_out) - new Date(shift.sign_in)) / 60000));
    const hours = (mins / 60).toFixed(1);
    return (
      <div style={s.screen}>
        <div style={s.submittedHero}>
          <div style={s.bigCheck}>✓</div>
          <div style={s.submittedTitle}>Checked Out</div>
          <div style={s.submittedSub}>{formatDate(shift.sign_out)}</div>
        </div>
        <div style={s.summaryCard}>
          <div style={s.summaryRow}><span style={s.summaryKey}>Building</span><span style={s.summaryVal}>{building.id} — {building.name}</span></div>
          <div style={s.summaryRow}><span style={s.summaryKey}>Worker</span><span style={s.summaryVal}>{shift.worker_name}</span></div>
          <div style={s.summaryRow}><span style={s.summaryKey}>Check In</span><span style={s.summaryVal}>{formatTime(shift.sign_in)}</span></div>
          <div style={s.summaryRow}><span style={s.summaryKey}>Check Out</span><span style={s.summaryVal}>{formatTime(shift.sign_out)}</span></div>
          <div style={s.summaryRow}><span style={s.summaryKey}>Duration</span><span style={s.summaryVal}>{mins} min ({hours} hrs)</span></div>
          {shift.maintenance_flag && (
            <div style={{ ...s.summaryRow, flexDirection: 'column', gap: 4 }}>
              <span style={{ ...s.summaryKey, color: '#f97316' }}>🔧 Issue reported</span>
              <span style={{ ...s.summaryVal, color: '#fdba74', fontSize: 13 }}>{shift.maintenance_note}</span>
            </div>
          )}
        </div>
        <button style={s.newShiftBtn} onClick={handleNewShift}>Check In to Another Building</button>
        <button style={s.signOutBtnLarge} onClick={signOut}>Sign Out</button>
      </div>
    );
  }

  if (phase === 'messages') {
    return <MessagesScreen onBack={() => setPhase('select')} />;
  }

  if (phase === 'help') {
    return (
      <div style={{ ...s.screen, padding: '16px 14px 40px' }}>
        <HelpGuide guide="cleaner" onBack={() => setPhase('select')} />
      </div>
    );
  }

  return (
    <BuildingSelectScreen
      profile={profile}
      onSelect={handleSelectBuilding}
      onInspect={handleInspect}
      onLogout={signOut}
      onOpenMessages={() => setPhase('messages')}
      onOpenHelp={() => setPhase('help')}
    />
  );
}
